import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Unlock, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Big, customer-facing rescue card. Shown to ANY signed-in non-subscribed user
 * (or anyone who clicks the link). One tap = unlock if they actually paid.
 *
 *   1. If they're signed in → calls /payment/recover (server-side webhook + PayPal API)
 *   2. If unauthed or step 1 fails → opens a dialog asking for their PayPal email
 *      and calls /payment/claim-by-email which works without a session.
 */
export default function PaidUnlockButton({ variant = "inline" }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const oneTapRecover = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payment/recover");
      if (r.data?.recovered) {
        toast.success("Unlocked! Welcome back.");
        await refresh();
        // Hard-reload so /content + Browse refetch with the new subscribed state.
        setTimeout(() => { window.location.replace("/browse?just_paid=1"); }, 400);
        return;
      }
    } catch (_) {}
    // Recover didn't find anything tied to this account — open the email rescue.
    setBusy(false);
    setOpen(true);
  };

  const claimByEmail = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter the email you used in PayPal.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/payment/claim-by-email", { email: email.trim() });
      if (r.data?.claimed) {
        toast.success("Unlocked! Sign in with Google using this email to start watching.");
        setOpen(false);
        // If they're already signed in with the matching email, hard-reload.
        if (user?.email?.toLowerCase() === email.trim().toLowerCase()) {
          setTimeout(() => { window.location.replace("/browse?just_paid=1"); }, 600);
        } else {
          // Otherwise tell them to sign in.
          setTimeout(() => { window.location.replace("/"); }, 1500);
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "No matching PayPal payment found. Double-check your email.");
    } finally {
      setBusy(false);
    }
  };

  const trigger = variant === "compact" ? (
    <button
      data-testid="paid-unlock-link"
      type="button"
      className="text-xs text-[#FFD700] hover:text-[#FFE873] underline decoration-dotted underline-offset-4"
    >
      Already paid? Unlock me
    </button>
  ) : (
    <Button
      data-testid="paid-unlock-btn"
      onClick={user ? oneTapRecover : () => setOpen(true)}
      disabled={busy}
      className="rounded-full bg-gradient-to-r from-[#E50914] to-[#F40612] hover:from-[#F40612] hover:to-[#E50914] text-white px-6 py-5 text-sm font-semibold shadow-lg shadow-[#E50914]/25 disabled:opacity-60"
    >
      {busy ? (
        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unlocking…</>
      ) : (
        <><Unlock className="w-4 h-4 mr-2" /> Already paid? Unlock me now</>
      )}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {variant === "compact" ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        // Trigger is the button itself; we open the dialog only if /recover fails.
        trigger
      )}

      <DialogContent
        data-testid="paid-unlock-dialog"
        className="bg-[#141419] border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Unlock className="w-5 h-5 text-[#FFD700]" />
            Already paid? Unlock by email
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={claimByEmail} className="space-y-4 mt-2">
          <p className="text-sm text-gray-300 leading-relaxed">
            Enter the email you used in PayPal when you subscribed. We'll match
            it to your payment and unlock your account immediately.
          </p>
          <Input
            data-testid="paid-unlock-email-input"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
            placeholder="your-paypal-email@example.com"
            autoFocus
            className="bg-black/40 border-white/20 text-white"
          />
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-400">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          <Button
            data-testid="paid-unlock-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[#E50914] hover:bg-[#F40612] text-white py-5 font-semibold"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching…</> : "Unlock my account"}
          </Button>
          <p className="text-[11px] text-gray-500 text-center">
            Still stuck? Email <span className="text-gray-300">support@youngblack.tv</span> with your PayPal receipt.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
